import { createContext, useContext, useEffect, useState } from "react";

type FontSize = "small" | "medium" | "large" | "extra-large";

type FontSizeProviderProps = {
  children: React.ReactNode;
  defaultFontSize?: FontSize;
  storageKey?: string;
};

type FontSizeProviderState = {
  fontSize: FontSize;
  setFontSize: (fontSize: FontSize) => void;
};

const initialState: FontSizeProviderState = {
  fontSize: "medium",
  setFontSize: () => null,
};

const FontSizeProviderContext =
  createContext<FontSizeProviderState>(initialState);

const fontSizeMap = {
  small: "0.875rem",
  medium: "1rem",
  large: "1.125rem",
  "extra-large": "1.25rem",
};

function applyFontSizeVariables(fontSize: FontSize) {
  const root = window.document.documentElement;
  const baseFontSize = fontSizeMap[fontSize];

  // Set CSS custom properties for different text sizes
  root.style.setProperty("--font-size-xs", `calc(${baseFontSize} * 0.75)`);
  root.style.setProperty("--font-size-sm", `calc(${baseFontSize} * 0.875)`);
  root.style.setProperty("--font-size-base", baseFontSize);
  root.style.setProperty("--font-size-lg", `calc(${baseFontSize} * 1.125)`);
  root.style.setProperty("--font-size-xl", `calc(${baseFontSize} * 1.25)`);
  root.style.setProperty("--font-size-2xl", `calc(${baseFontSize} * 1.5)`);
  root.style.setProperty("--font-size-3xl", `calc(${baseFontSize} * 1.875)`);
  root.style.setProperty("--font-size-4xl", `calc(${baseFontSize} * 2.25)`);
}

export function FontSizeProvider({
  children,
  defaultFontSize = "medium",
  storageKey = "app-font-size",
  ...props
}: FontSizeProviderProps) {
  const [fontSize, setFontSize] = useState<FontSize>(
    () => (localStorage.getItem(storageKey) as FontSize) || defaultFontSize,
  );

  useEffect(() => {
    applyFontSizeVariables(fontSize);
  }, [fontSize]);

  const value = {
    fontSize,
    setFontSize: (fontSize: FontSize) => {
      localStorage.setItem(storageKey, fontSize);
      setFontSize(fontSize);
    },
  };

  return (
    <FontSizeProviderContext.Provider {...props} value={value}>
      {children}
    </FontSizeProviderContext.Provider>
  );
}

export const useFontSize = () => {
  const context = useContext(FontSizeProviderContext);

  if (context === undefined)
    throw new Error("useFontSize must be used within a FontSizeProvider");

  return context;
};
